import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import PlanosHero from './components/PlanosHero';
import PlanosGrid from './components/PlanosGrid';
import DepoimentosPlanos from './components/DepoimentosPlanos';
import ComparacaoPlanos from './components/ComparacaoPlanos';
import ExerciciosPNL from './components/ExerciciosPNL';
import ExerciciosAvulsos from './components/ExerciciosAvulsos';
import ExperienciasComplementaresPlanos from './components/ExperienciasComplementaresPlanos';
import MeditacoesGuiadas from './components/MeditacoesGuiadas';
import AssinaturaMensal from './components/AssinaturaMensal';
import ExperienciasEspeciais from './components/ExperienciasEspeciais';
import FAQExperiencias from './components/FAQExperiencias';
import PlanosFooter from './components/PlanosFooter';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const planosSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Planos e Preços | Carta do Eu Futuro',
    url: `${SITE_URL}/planos`,
    inLanguage: 'pt-BR',
    description: 'Conheça os planos do Carta do Eu Futuro. Acesso a exercícios de PNL, meditações guiadas, caderno de gratidão e comunidade de transformação pessoal.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Planos', item: `${SITE_URL}/planos` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Planos Carta do Eu Futuro',
    description: 'Planos de assinatura para acesso à plataforma de desenvolvimento pessoal.',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Product',
          name: 'Plano Essencial',
          description: 'Acesso às funcionalidades essenciais da plataforma de desenvolvimento pessoal com exercícios de PNL e caderno de gratidão.',
          brand: {
            '@type': 'Brand',
            name: 'Carta do Eu Futuro',
          },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/planos`,
          },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Product',
          name: 'Plano Poder Total',
          description: 'Acesso completo a todos os exercícios de PNL, meditações guiadas e comunidade elite de transformação pessoal.',
          brand: {
            '@type': 'Brand',
            name: 'Carta do Eu Futuro',
          },
          offers: {
            '@type': 'Offer',
            price: '97.00',
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/planos`,
            priceValidUntil: '2025-12-31',
          },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Product',
          name: 'Plano Elite Ilimitado',
          description: 'Acesso ilimitado com mentorias ao vivo exclusivas com Bia Holmes e suporte prioritário.',
          brand: {
            '@type': 'Brand',
            name: 'Carta do Eu Futuro',
          },
          offers: {
            '@type': 'Offer',
            price: '1970.00',
            priceCurrency: 'BRL',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/planos`,
            priceValidUntil: '2025-12-31',
          },
        },
      },
    ],
  },
];

export default function PlanosPage() {
  const [searchParams] = useSearchParams();
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');

  useEffect(() => {
    // Verificar se veio da expiração do acesso gratuito
    const expired = searchParams.get('expired');
    const message = searchParams.get('message');
    
    if (expired === 'true') {
      setShowExpiredModal(true);
      setExpiredMessage(message || 'Seu período gratuito de 9 dias terminou — assine para continuar');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-luxury-black">
      <SEOHead
        title="Planos e Preços | PNL, Meditação e Gratidão — Carta do Eu Futuro"
        description="Escolha o plano ideal para sua transformação pessoal. Acesso a exercícios de PNL, meditações guiadas, caderno de gratidão digital e comunidade de alto impacto."
        keywords="planos carta do eu futuro, preços, assinatura, PNL, meditação, desenvolvimento pessoal"
        canonical="/planos"
        ogImage="https://public.readdy.ai/ai/img_res/a6e50b9bdeca93b95c0c0309ca6bb261.jpg"
        ogType="website"
        schema={planosSchema}
      />
      {/* Modal de Acesso Expirado */}
      {showExpiredModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-[#111111] border border-[#D4AF37]/25 rounded-3xl max-w-2xl w-full p-8 md:p-12 animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#D4AF37]/15 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-4xl text-[#D4AF37]"></i>
              </div>
              <h2 className="font-['Playfair_Display'] text-3xl text-white mb-4">
                Seu Período Gratuito Terminou
              </h2>
              <p className="text-lg text-white/55 leading-relaxed mb-6 font-light font-['Montserrat']">
                {expiredMessage}
              </p>
              <div className="bg-white/[0.05] border border-[#D4AF37]/20 rounded-xl p-6 mb-6">
                <h3 className="font-['Playfair_Display'] text-xl text-white mb-3">
                  Continue sua jornada de transformação!
                </h3>
                <p className="text-base text-white/45 leading-relaxed font-['Montserrat'] font-light">
                  Você experimentou o poder da gratidão e das técnicas de PNL. Agora é hora de continuar essa transformação com acesso completo e ilimitado!
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-white/[0.04] border border-[#D4AF37]/15 rounded-xl">
                <div className="w-12 h-12 bg-[#D4AF37]/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-infinite-line text-2xl text-[#D4AF37]"></i>
                </div>
                <h4 className="text-base font-bold text-white mb-1 font-['Playfair_Display']">Acesso Ilimitado</h4>
                <p className="text-sm text-white/35 font-['Montserrat']">Sem limite de tempo</p>
              </div>

              <div className="text-center p-4 bg-white/[0.04] border border-[#D4AF37]/15 rounded-xl">
                <div className="w-12 h-12 bg-[#D4AF37]/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-trophy-line text-2xl text-[#D4AF37]"></i>
                </div>
                <h4 className="text-base font-bold text-white mb-1 font-['Playfair_Display']">Todas as Conquistas</h4>
                <p className="text-sm text-white/35 font-['Montserrat']">Sistema completo</p>
              </div>

              <div className="text-center p-4 bg-white/[0.04] border border-[#D4AF37]/15 rounded-xl">
                <div className="w-12 h-12 bg-[#D4AF37]/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-2xl text-[#D4AF37]"></i>
                </div>
                <h4 className="text-base font-bold text-white mb-1 font-['Playfair_Display']">Backup Seguro</h4>
                <p className="text-sm text-white/35 font-['Montserrat']">Dados protegidos</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowExpiredModal(false)}
                className="flex-1 px-8 py-4 bg-[#D4AF37] text-black text-base font-semibold rounded-xl hover:bg-[#C5A028] transition-all duration-300 whitespace-nowrap cursor-pointer font-['Montserrat'] tracking-wide"
              >
                <i className="ri-star-line mr-2"></i>
                Ver Planos
              </button>
              <button
                onClick={() => window.history.back()}
                className="flex-1 px-8 py-4 bg-white/[0.06] border border-white/10 text-white/60 text-base font-semibold rounded-xl hover:bg-white/[0.08] transition-colors whitespace-nowrap cursor-pointer font-['Montserrat']"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}

      <PlanosHero />
      <PlanosGrid />
      <DepoimentosPlanos />
      <ComparacaoPlanos />
      <ExerciciosPNL />
      <ExerciciosAvulsos />
      <MeditacoesGuiadas />
      <AssinaturaMensal />
      <ExperienciasEspeciais />
      <ExperienciasComplementaresPlanos />
      <FAQExperiencias />
      <PlanosFooter />

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
